import { CustomDialog } from '../../../../components/Dialogs/CustomDialog';
import { IManager } from '../../../../common/user/interfaces/manager';

import styles from './styles.module.scss';
import { documentsToNameList } from '../../../../functions/documents/documentUtils';
import ButtonWithArrowIcon from '../../../../components/Buttons/ButtonWithArrowIcon';
import * as React from 'react';
import ClipboardCopy from '../../../../components/ClipboardCopy';
import { linkToCopy } from '../../../../functions/linkUtils';
import { AnimatedLoaderModal } from '../../../../components/Loaders/AnimatedLoader';
import { IReviewDocument } from '../../../../common/request/interfaces/reviewDocument';
import { useContext, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../index';
import { getCompanyNameForSms } from '../../../../functions/get-company-name-for-sms';
import { Textarea } from '../../../../components/Form/Textarea';
import OutlinedButton from '../../../../components/Buttons/OutlinedButton';

type InputProps = {
  isLoading: boolean;
  isDelivered: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  request: IReviewDocument;
  manager: IManager;
  companyName: string;
  link: string;
  title?: string;
  onSendMessage: (text: string) => void;
};

export const ConfirmRequestDialog = ({
  isLoading = false,
  isDelivered = false,
  open = false,
  setOpen,
  request,
  link,
  title = 'Confirm Request',
  onSendMessage,
}: InputProps) => {
  const company = useContext(CompanyContext);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const defaultMessageContent = useMemo(() => {
    return `Hello, ${request.candidateFullName}, it’s ${getCompanyNameForSms(
      company
    )}. Please upload your ${documentsToNameList(
      request.documentsNeeded
    )} here:`;
  }, [request.candidateFullName, company, request.documentsNeeded]);

  const defaultMessageFooter = useMemo(() => {
    return ` ${link} Reply STOP to unsubscribe. Msg & Data rates may apply.`;
  }, [link]);

  const [message, setMessage] = useState<string>(
    defaultMessageContent + defaultMessageFooter
  );
  const [messageTemp, setMessageTemp] = useState<string>(defaultMessageContent);

  const handleChangeMessage = (e: any) => {
    setMessageTemp(e.target.value);
  };

  const handleSaveMessage = () => {
    setMessage(messageTemp + defaultMessageFooter);
    setIsEdit(false);
  };

  return (
    <CustomDialog open={open} setOpen={setOpen} title={title}>
      {isLoading ? (
        <div>
          {isDelivered && (
            <div className={styles.messageSent}>Message Sent</div>
          )}
          <AnimatedLoaderModal
            isLoading={!isDelivered}
            classNameWrapper={styles.loaderWrapper}
          />
        </div>
      ) : (
        <div className={styles.root}>
          {isEdit ? (
            <div className={styles.editRoot}>
              <Textarea
                minRows={3}
                maxLength={200}
                value={messageTemp}
                onChange={handleChangeMessage}
              />
              <div className={styles.message}>
                {messageTemp}
                <br />
                <span className={styles.link}>{link}</span> Reply STOP to
                unsubscribe. Msg & Data rates may apply.
              </div>
              <div className={styles.actions}>
                <OutlinedButton
                  onClick={() => {
                    setIsEdit(false);
                    setMessageTemp(defaultMessageContent);
                  }}
                >
                  Cancel
                </OutlinedButton>
                <ButtonWithArrowIcon onClick={handleSaveMessage}>
                  Save
                </ButtonWithArrowIcon>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.description}>
                You’re requesting{' '}
                <b>{documentsToNameList(request.documentsNeeded)}</b> from{' '}
                <b>{request.candidateFullName}</b>. We’ll email you as soon as
                the verified documents are uploaded.
              </div>
              <div className={styles.message}>
                {messageTemp}
                <br />
                <span className={styles.link}>{link}</span> Reply STOP to
                unsubscribe. Msg & Data rates may apply.
              </div>
              <div className={styles.actions}>
                <ClipboardCopy
                  label="Driver Unique Upload Link"
                  isActive={true}
                  copyText={linkToCopy(link)}
                />

                <OutlinedButton
                  onClick={() => {
                    setIsEdit(true);
                  }}
                >
                  Edit Message
                </OutlinedButton>

                <ButtonWithArrowIcon
                  onClick={() =>
                    onSendMessage(message.replaceAll(/(\s)+/gim, ' '))
                  }
                >
                  Send Message
                </ButtonWithArrowIcon>
              </div>
            </>
          )}
        </div>
      )}
    </CustomDialog>
  );
};
